import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { defineMessages } from 'react-intl';
import Ref from '../../components/Ref';
import Header from '../../components/Header';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Message from '../../components/Message';
import InputsTable from '../../components/InputsTable';
import { withIntegerInLimits, withFloatInLimits } from './InputValidators';
import { withErrorMessage } from './renderers';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';

import { academicThresholdColumns } from './columns';
import './styles.css';

export const inputIds = {
  gpa: 'gpa',
  sat: 'sat',
  act: 'act',
};

const messages = defineMessages({
  validationInvalidValue: {
    id: 'amnext.threshold.settings.invalid.value',
    defaultMessage: 'Invalid value',
  },
  validationDefaultsRequired: {
    id: 'amnext.threshold.settings.defaults.required',
    defaultMessage: 'Please set default filter values.',
  },
  validationDefaultsInvalidTitle: {
    id: 'amnext.threshold.settings.defaults.invalid.title',
    defaultMessage: 'Invalid Default Filter Values',
  },
  validationDefaultsInvalid: {
    id: 'amnext.threshold.settings.defaults.invalid',
    defaultMessage: 'Please review your default filter values to ensure they are in the correct format.',
  },
  validationIsAllEmpty: {
    id: 'amnext.threshold.settings.isAllEmpty.error',
    defaultMessage: 'Please enter at least one academic threshold value per audience',
  },
  inputsTableCaption: {
    id: 'amnext.threshold.settings.table.caption',
    defaultMessage: 'Subscriptions thresholds editor table',
  },
  formSubmitLabel: {
    id: 'amnext.threshold.settings.label.save',
    defaultMessage: 'Save',
  },
});

const InputGPA = withErrorMessage(withFloatInLimits(Input, 0.1, 4.0, 1));
const InputSAT = withErrorMessage(withIntegerInLimits(Input, 400, 1600));
const InputACT = withErrorMessage(withIntegerInLimits(Input, 1, 36));

function validateIsAllEmpty(data) {
  const { gpa, sat, act } = data;
  return !gpa && !sat && !act;
}

function validateThresholds(data, intl) {
  const { sat, gpa, act } = data;
  const pMinSat = parseInt(sat, 10);
  const pMinGpa = parseFloat(gpa);
  const pMinAct = parseInt(act, 10);
  const invalidValueMessage = intl.formatMessage(messages.validationInvalidValue);
  const errors = {
    gpa: gpa && !(pMinGpa >= 0.1 && pMinGpa <= 4.0) && invalidValueMessage,
    sat: sat && !(pMinSat >= 400 && pMinSat <= 1600) && invalidValueMessage,
    act: act && !(pMinAct >= 1 && pMinAct <= 36) && invalidValueMessage,
  };

  const isAllEmpty = validateIsAllEmpty({ gpa, sat, act });

  return {
    isInvalid: Object.keys(errors).some((key) => errors[key]),
    errors,
    isAllEmpty,
  };
}

function parseSubscriptions(subscriptions, defaults = {}, intl) {
  const errorsInDefaults = validateThresholds(defaults, intl);
  const parsed = subscriptions.map((sub) => {
    const {
      node: {
        id,
        version,
        type,
        majors,
        connection,
        academicThreshold,
        audience,
      },
    } = sub;

    const useDefaults = !_get(academicThreshold, 'thresholds') && !(errorsInDefaults.isInvalid || errorsInDefaults.isAllEmpty);
    const gpa = _get(academicThreshold, 'thresholds.gpa') || '';
    const sat = _get(academicThreshold, 'thresholds.sat') || '';
    const act = _get(academicThreshold, 'thresholds.act') || '';

    return {
      id,
      version,
      type,
      majors,
      connection,
      audience,
      academicThreshold,
      enabled: academicThreshold.enabled,
      useDefault: useDefaults,
      gpa: useDefaults ? defaults.gpa: gpa,
      sat: useDefaults ? defaults.sat: sat,
      act: useDefaults ? defaults.act: act,
    };
  });

  return {
    subscriptions: parsed,
    errors: errorsInDefaults.errors,
  };
}

class ConfigureThresholds extends React.Component {
  static propTypes = {
    defaults: PropTypes.object,
    subscriptions: PropTypes.array,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
  };

  static getDerivedStateFromProps(props, state) {
    const { prevSubscriptions, prevDefaults } = state;
    const newState = {};
    if (props.defaults && prevDefaults !== props.defaults) {
      newState.errors = {};
      newState.prevDefaults = props.defaults;
      const { gpa = '', sat = '', act = '' } = props.defaults.academicThresholds || {};
      newState.gpa = gpa;
      newState.act = act;
      newState.sat = sat;
    }

    if (props.subscriptions && prevSubscriptions !== props.subscriptions) {
      const newDefaults = {
        gpa: newState.gpa || state.gpa,
        act: newState.act || state.act,
        sat: newState.sat || state.sat,
      };
      const { intl } = props;
      const parsedSubscriptions = parseSubscriptions(props.subscriptions, newDefaults, intl);
      newState.subscriptions = parsedSubscriptions.subscriptions;
      newState.errors = parsedSubscriptions.errors;
      newState.prevSubscriptions = props.subscriptions;
    }

    return newState;
  }

  constructor(props) {
    super(props);
    this.state = {
      gpa: '',
      sat: '',
      act: '',
      changed: {
        gpa: false,
        sat: false,
        act: false,
      },
      errors: {},
      error: null,
      subscriptions: [],
      loaded: false,
      prevSubscriptions: null,
      prevDefaults: null,
    };
  }

  handleChange = (event, props) => {
    const { name, value, error, isInvalid } = props;
    this.setState((prevState) => ({
      [name]: isInvalid ? prevState[name]: value,
      changed: {
        ...prevState.changed,
        [name]: true,
      },
      errors: {
        ...prevState.errors,
        [name]: error,
      },
    }));
  }

  handleChangeCells = (event, sprops) => {
    const { intl } = this.props;
    const { id, name: oName, value, error, isInvalid } = sprops;
    // eslint-disable-next-line react/no-access-state-in-setstate
    const defaults = validateThresholds(this.state, intl);
    const { sat, act, gpa } = this.state;

    // eslint-disable-next-line react/no-access-state-in-setstate
    const newSubscriptions = this.state.subscriptions.map((sub) => {
      if (sub.id === id) {
        const name = oName.split('.')[1];
        if (name === 'gpa' || name === 'act' || name === 'sat') sub.useDefault = false;
        if (name === 'enabled') delete sub.isAllEmpty;
        if (name === 'useDefault') {
          if (defaults.isInvalid || defaults.isAllEmpty) {
            this.setState({
              error: {
                title: intl.formatMessage(messages.validationDefaultsInvalidTitle),
                body: intl.formatMessage(messages.validationDefaultsInvalid),
              },
            });
            return {
              ...sub,
              errors: {
                ...sub.errors,
                [name]: intl.formatMessage(messages.validationDefaultsRequired),
              },
            };
          } else {
            this.setState({
              error: null,
            });
          }
          if (value) {
            return {
              ...sub,
              errors: {},
              error: null,
              useDefault: true,
              temp: {
                act: sub.act,
                gpa: sub.gpa,
                sat: sub.sat,
              },
              act,
              gpa,
              sat,
            };
          } else {
            return {
              ...sub,
              ...(sub.temp || {}),
              useDefault: false,
            };
          }
        }
        return {
          ...sub,
          [name]: isInvalid ? sub[name]: value,
          errors: {
            ...sub.errors,
            [name]: error,
          },
        };
      }
      return sub;
    });
    this.setState({
      subscriptions: newSubscriptions,
      errors: defaults.errors,
    });
  }

  validateCells(subscriptions) {
    const { intl } = this.props;
    const errorsInDefaults = validateThresholds(this.state, intl);
    let needDefaults = false;
    const newSubscriptions = subscriptions.map((sub) => {
      if (sub.enabled) {
        sub.isAllEmpty = validateIsAllEmpty({ gpa: sub.gpa, sat: sub.sat, act: sub.act });
      } else {
        delete sub.isAllEmpty;
      }
      const newErrors = this._validateTableFields({
        useDefault: sub.useDefault,
        enabled: sub.enabled,
        gpa: sub.gpa,
        sat: sub.sat,
        act: sub.act,
      });

      if (newErrors.checkDefaults) {
        newErrors.useDefault = (errorsInDefaults.isInvalid || errorsInDefaults.isAllEmpty) &&
          intl.formatMessage(messages.validationDefaultsRequired);
        delete newErrors.checkDefaults;

        if (newErrors.useDefault) {
          needDefaults = true;
        }
      }
      return {
        ...sub,
        errors: {
          useDefault: newErrors.useDefault,
          ...newErrors.errors,
        },
      };
    });

    return {
      invalidDefaults: errorsInDefaults.isInvalid,
      defaults: errorsInDefaults,
      subscriptions: newSubscriptions,
      needDefaults,
    };
  }

  _validateTableFields(data) {
    const { useDefault, enabled, sat, gpa, act } = data;
    if (enabled) {
      if (!useDefault) {
        return this._validateFields({ sat, gpa, act });
      } else {
        return {
          checkDefaults: true,
        };
      }
    }

    return {};
  }

  _validateFields(data) {
    const { intl } = this.props;
    return validateThresholds(data, intl);
  }

  _validateForm = () => {
    const { intl, onError } = this.props;
    const { subscriptions } = this.state;
    const res = this.validateCells(subscriptions);
    const { defaults } = res;

    const errorInSubscriptions = res.subscriptions.some((sub) => sub.errors && Object.keys(sub.errors).some((key) => sub.errors[key]));

    const errorIsAllEmpty = res.subscriptions.some((sub) => sub.isAllEmpty);

    if (errorInSubscriptions || defaults.isInvalid || errorIsAllEmpty) {
      this.setState({
        subscriptions: res.subscriptions,
        errors: defaults.errors,
      });

      if (errorIsAllEmpty) {
        onError({
          message: intl.formatMessage(messages.validationIsAllEmpty),
        });
      }

      const error = (defaults.isInvalid || res.needDefaults) ?
        {
          title: intl.formatMessage(messages.validationDefaultsInvalidTitle),
          body: intl.formatMessage(messages.validationDefaultsInvalid),
        } :
        null;

      this.setState({
        error,
      });

      if (error && this.inlineErrorRef) {
        setTimeout(() => this.inlineErrorRef.scrollIntoView());
      }
    } else {
      const { gpa, sat, act } = this.state;
      const defaultThresholds = { gpa, sat, act };
      const newSubscriptions = subscriptions.map((sub) => ({
        id: sub.id,
        version: sub.version,
        enabled: sub.enabled,
        useDefaults: sub.useDefault,
        gpa: sub.gpa,
        sat: sub.sat,
        act: sub.act,
      }));

      this.setState({
        error: null,
      });

      return {
        defaults: defaultThresholds,
        subscriptions: newSubscriptions,
      };
    }
    return null;
  }

  handleSubmit = (e, props) => {
    const result = this._validateForm();
    const { onSubmit } = this.props;
    if (result) {
      onSubmit(result.defaults, result.subscriptions);
    }
    e.preventDefault();
    return false;
  }

  promptSaveChanges = () => {
    const result = this._validateForm();
    const { onSubmit } = this.props;
    if (result) {
      return onSubmit(result.defaults, result.subscriptions);
    }
    /*
     * This rejection is to let UnsavedChangesPrompt that should not change the page
     * The value is not used for any purpose
     */
    return Promise.reject(new Error('Invalid data'));
  }

  isDirty = () => {
    const { defaults } = this.props;
    if (!defaults) return false;
    if (!defaults.academicThresholds) return false;
    const { academicThresholds } = defaults;

    const thresholdFieldNames = ['act', 'sat', 'gpa'];

    const defaultsDirty = thresholdFieldNames.some((name) => academicThresholds[name] !== this.state[name]);
    if (defaultsDirty) {
      return true;
    }
    const subscriptionsDirty = this.props.subscriptions.some((original, index) => {
      const { node: { academicThreshold } } = original;
      const current = this.state.subscriptions[index];

      const enabledDirty = academicThreshold.enabled !== current.enabled;
      const emptyThresholsDirty = !academicThreshold.thresholds && !current.useDefault;

      let thresholdsDirty = false;
      let thresholdsDirtyFields = false;

      if (academicThreshold.thresholds) {
        thresholdsDirty = current.useDefault;
        const { thresholds } = academicThreshold;
        thresholdsDirtyFields = thresholdFieldNames.some((name) => thresholds[name] !== current[name]);
      }
      return enabledDirty || emptyThresholsDirty || thresholdsDirty || thresholdsDirtyFields;
    });
    return subscriptionsDirty;
  }

  handleInlineErrorRef = (ref) => {
    this.inlineErrorRef = ref;
  };

  render() {
    const { intl } = this.props;
    const { error, errors, sat, gpa, act, subscriptions } = this.state;
    const isDirty = this.isDirty();
    return (
      <React.Fragment>
        <Ref innerRef={this.handleInlineErrorRef}>
          <div>
            { error && <Message negative><Message.Header>{error.title}</Message.Header>{error.body}</Message> }
          </div>
        </Ref>
        <Header as="h2" size="medium">
          Default Filter Values
        </Header>
        <Form onSubmit={this.handleSubmit}>
          <Form.Group widths="equal" inline>
            <Form.Field styleName="field">
              <label htmlFor={inputIds.gpa}>Minimum GPA <br />(4.0 scale)</label>
              <div styleName="default-field">
                <InputGPA
                  id={inputIds.gpa}
                  onChange={this.handleChange}
                  value={gpa}
                  name="gpa"
                  error={errors.gpa}
                  styleName="inputs"
                />
              </div>
            </Form.Field>
            <Form.Field
              styleName="field"
            >
              <label htmlFor={inputIds.sat}>
                Minimum SAT Combined<br />(1600 scale)
              </label>
              <div styleName="default-field">
                <InputSAT
                  id={inputIds.sat}
                  onChange={this.handleChange}
                  name="sat"
                  value={sat}
                  error={errors.sat}
                  styleName="inputs"
                />
              </div>
            </Form.Field>
            <Form.Field
              styleName="field"
            >
              <label htmlFor={inputIds.act}>
                Minimum ACT Combined
              </label>
              <div styleName="default-field">
                <InputACT
                  id={inputIds.act}
                  onChange={this.handleChange}
                  value={act}
                  name="act"
                  error={errors.act}
                  styleName="inputs"
                />
              </div>
            </Form.Field>
          </Form.Group>
          <Header as="h2" size="medium">Enable Academic Filters </Header>
          <InputsTable
            columns={academicThresholdColumns}
            data={subscriptions}
            onChange={this.handleChangeCells}
            caption={intl.formatMessage(messages.inputsTableCaption)}
            defaults={{ sat, gpa, act }}
          />
          <div styleName="form-footer">
            <Button primary color="teal" type="submit">{intl.formatMessage(messages.formSubmitLabel)}</Button>
          </div>
        </Form>
        <UnsavedChangesPrompt
          when={isDirty}
          saveChanges={this.promptSaveChanges}
          closeable
        />
      </React.Fragment>
    );
  }
}

export default ConfigureThresholds;
